/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useUserContext } from '@vitvio/user-context';
import {
  useStylesCss,
  CoreText,
  type PaddingProps,
  type MarginProps,
  type ColorProps,
  type FontProps,
  type FontToken,
  type SemanticColorToken,
} from '@vitvio/design-system';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import readableTimezone from './helpers/readableTimezone';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(readableTimezone);

interface CoreClockProps extends PaddingProps, MarginProps, ColorProps, FontProps {
  showSeconds?: boolean;
  showTimezone?: boolean;
  separator?: string;
  force24h?: boolean;
  blinkingColon?: boolean;
  /** Font token for timezone (when different from main clock font) */
  timezoneFont?: FontToken;
  /** Color token for timezone (when different from main clock color) */
  timezoneColor?: SemanticColorToken;
}

interface TimeState {
  hours: string;
  minutes: string;
  seconds: string;
  ampm: string;
  timezone: string;
  isEvenSecond: boolean;
}

const STYLE_GROUPS = ['colors', 'font', 'padding', 'margin'] as const;

export function CoreClock({
  force24h = false,
  showSeconds,
  showTimezone,
  separator = ':',
  blinkingColon = true,
  timezoneFont,
  timezoneColor = 'fg.muted',
  ...styleProps
}: CoreClockProps) {
  const { timeFormat, hospitalTimezone } = useUserContext();
  const defaultTimezone = 'UTC';

  const activeTimezone = useMemo(() => {
    if (!hospitalTimezone) {
      return defaultTimezone;
    }

    try {
      dayjs().tz(hospitalTimezone);
      return hospitalTimezone;
    } catch {
      return defaultTimezone;
    }
  }, [hospitalTimezone]);

  const is12hr = !force24h && timeFormat === '12hr';

  const formatTime = useCallback((): TimeState => {
    const now = dayjs().tz(activeTimezone);
    return {
      hours: now.format(is12hr ? 'h' : 'HH'),
      minutes: now.format('mm'),
      seconds: now.format('ss'),
      ampm: is12hr ? now.format(' a') : '',
      timezone: showTimezone ? now.format(' (z)') : '',
      isEvenSecond: now.second() % 2 === 0,
    };
  }, [activeTimezone, is12hr, showTimezone]);

  const [time, setTime] = useState<TimeState>(formatTime);
  const prevTimeRef = useRef<TimeState>(time);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const scheduleNextTick = () => {
      const current = dayjs();
      const nextSecond = current.add(1, 'second').startOf('second');
      const msUntilNext = nextSecond.diff(current);

      timeoutId = setTimeout(() => {
        const newTime = formatTime();

        // Only update state if visible values changed
        const prev = prevTimeRef.current;
        const timeChanged =
          prev.hours !== newTime.hours ||
          prev.minutes !== newTime.minutes ||
          (showSeconds && prev.seconds !== newTime.seconds) ||
          prev.ampm !== newTime.ampm ||
          prev.timezone !== newTime.timezone ||
          (blinkingColon && !showSeconds && prev.isEvenSecond !== newTime.isEvenSecond);

        if (timeChanged) {
          prevTimeRef.current = newTime;
          setTime(newTime);
        }

        scheduleNextTick();
      }, msUntilNext);
    };

    scheduleNextTick();

    return () => clearTimeout(timeoutId);
  }, [formatTime, showSeconds, blinkingColon]);

  const colonVisible = !blinkingColon || showSeconds || time.isEvenSecond;

  const stylesCss = useStylesCss(styleProps, STYLE_GROUPS);

  const hasCustomTimezoneStyle = timezoneFont || timezoneColor;

  return (
    <span css={stylesCss}>
      {time.hours}
      <span style={{ opacity: colonVisible ? 1 : 0 }}>{separator}</span>
      {time.minutes}
      {showSeconds && (
        <>
          {separator}
          {time.seconds}
        </>
      )}
      {time.ampm}
      {hasCustomTimezoneStyle ? (
        <CoreText font={timezoneFont} color={timezoneColor}>
          {time.timezone}
        </CoreText>
      ) : (
        time.timezone
      )}
    </span>
  );
}
